export type Locale = 'en' | 'ar';

export const copy = {
  en: {
    nav: {
      home: 'Home',
      shop: 'Shop',
      news: 'News',
      cosmetics: 'Cosmetics',
      events: 'Events',
      settings: 'Settings'
    },
    common: {
      preview: 'Preview mode',
      viewAll: 'View all',
      backHome: 'Back home',
      source: 'Source',
      planned: 'Planned',
      demo: 'Demo content',
      live: 'Live',
      liveData: 'Live data',
      loading: 'Loading…',
      unavailable: 'Unavailable',
      fallback: 'Fallback'
    },
    home: {
      eyebrow: 'FORTNITE INTELLIGENCE',
      title: 'Everything in the loop.',
      body: 'Track the shop, explore cosmetics and stay updated through one clean Fortnite hub.',
      shopButton: 'Explore the shop',
      newsButton: 'Latest updates',
      briefing: 'Your Fortnite briefing',
      briefingBody: 'A focused snapshot of what matters today.',
      shopTitle: 'Shop preview',
      newsTitle: 'Latest stories',
      statusTitle: 'Fortnite status',
      statusBody: 'Checking the official Epic Games status feed.',
      statusLoading: 'Checking status…',
      statusUnavailable: 'Status temporarily unavailable',
      statusOperational: 'Fortnite services are operational',
      statusAttention: 'Fortnite status needs attention',
      statusLive: 'Live from Epic Games Public Status',
      sourceCount: 'planned trusted sources',
      liveSources: 'live source checks',
      liveDesk: 'LIVE DESK',
      now: 'Updated from NEXUS sources',
      nexusStory: 'NEXUS STORY',
      latestUpdate: 'LATEST UPDATE',
      shopFreshness: 'Shop refreshed',
      editorial: 'Published stories',
      seasonClock: 'SEASON COUNTDOWN',
      seasonEnds: 'Official end date:',
      exactTimePending: 'Epic currently publishes the end date in Eastern Time without an exact hour. NEXUS counts down to the start of that date and will update when Epic publishes a precise time.',
      endsToday: 'The season ends today (Eastern Time).',
      ended: 'This season end date has passed.',
      days: 'Days',
      hours: 'Hours',
      minutes: 'Minutes',
      officialSeasonSource: 'Official Battle Pass source'
    },
    notice: 'Epic service status comes from Epic Games Public Status. Shop, cosmetics and the in-game BR feed are retrieved server-side through Fortnite-API. Official Fortnite articles enter NEXUS through a review-first editorial workflow.',
    shop: {
      eyebrow: 'ITEM SHOP',
      title: 'Item Shop',
      body: 'A live server-side view of the current Fortnite shop with prices, item art and source freshness.',
      lastSync: 'Last NEXUS fetch',
      live: 'Live shop feed connected',
      error: 'Live shop feed is temporarily unavailable',
      fallback: 'Showing layout fallback because the live shop source could not be reached.'
    },
    news: {
      eyebrow: 'NEWS LAYER',
      title: 'News',
      body: 'NEXUS Originals and approved source briefs appear alongside the live Fortnite in-game Battle Royale feed.',
      feedLabel: 'Live in-game Battle Royale feed via Fortnite-API',
      official: 'Open official Fortnite news',
      originals: 'NEXUS stories',
      original: 'NEXUS ORIGINAL',
      externalBrief: 'SOURCE BRIEF'
    },
    cosmetics: {
      eyebrow: 'DATABASE',
      title: 'Cosmetics',
      body: 'Search the live Fortnite cosmetics data. With no search term, NEXUS shows recently added cosmetics.',
      search: 'Search cosmetics',
      filter: 'Category',
      all: 'All categories',
      noResults: 'No matching items',
      results: 'results',
      error: 'Cosmetics data is temporarily unavailable'
    },
    events: {
      eyebrow: 'EVENTS + COMPETITIVE',
      title: 'Events',
      body: 'Season timing is live now. Additional event and tournament countdowns will appear only after source and timezone validation.',
      pending: 'The season clock is live. Competitive and special-event countdowns stay pending until their source and timezone rules are verified.',
      sourcePlan: 'Planned sync schedule',
      newsSource: 'Fortnite News',
      statusSource: 'Fortnite Status',
      competitiveSource: 'Competitive'
    },
    settings: {
      eyebrow: 'PERSONALIZE NEXUS',
      title: 'Settings',
      body: 'A modern version of the customization panel from the original 2023 site, rebuilt around useful preferences instead of gimmicks.',
      preferencesEyebrow: 'PREFERENCES',
      preferences: 'Your experience',
      language: 'Language',
      languageBody: 'Choose the interface language and keep it saved on this device.',
      reduceMotion: 'Reduce motion',
      reduceMotionBody: 'Disable most interface transitions and motion effects.',
      compactMode: 'Compact cards',
      compactModeBody: 'Fit more shop and cosmetic cards on screen.',
      reset: 'Reset preferences',
      legacyEyebrow: 'LEGACY REVIVAL',
      legacyTitle: 'What came back',
      legacyBody: 'The 2023 Fortnite Status site had good ideas hidden inside a rough implementation. NEXUS is bringing back the useful ones with better data and cleaner behavior.',
      legacyCountdowns: 'Countdowns',
      legacyFaq: 'FAQ',
      legacyChangelog: 'Changelog',
      legacyPreferences: 'Saved preferences',
      faqEyebrow: 'QUESTION + ANSWER',
      faqTitle: 'FAQ',
      changelogEyebrow: 'SITE HISTORY',
      changelogTitle: 'What changed'
    },
    admin: {
      title: 'System Health',
      body: 'A read-only view of source availability, D1 sync history and the last known successful runs.',
      readOnly: 'Read only',
      authNote: 'Protected by Cloudflare Access. This page is currently read-only while write controls remain disabled until the next admin-security layer is complete.',
      refresh: 'Refresh',
      unavailable: 'System health is temporarily unavailable',
      database: 'D1 database',
      connected: 'Connected',
      disconnected: 'Disconnected',
      sources: 'Healthy sources',
      lastCheck: 'Last check',
      healthy: 'Healthy',
      degraded: 'Degraded',
      latency: 'Latency',
      lastSuccess: 'Last success',
      failures: 'Failures',
      items: 'Last item count',
      itemsShort: 'items',
      recentRuns: 'Recent sync runs',
      noRuns: 'No sync runs have been recorded yet'
    },
    footer: 'Independent Fortnite companion. Not affiliated with Epic Games.'
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      shop: 'المتجر',
      news: 'الأخبار',
      cosmetics: 'العناصر',
      events: 'الفعاليات',
      settings: 'الإعدادات'
    },
    common: {
      preview: 'نسخة تجريبية',
      viewAll: 'عرض الكل',
      backHome: 'العودة للرئيسية',
      source: 'المصدر',
      planned: 'مخطط',
      demo: 'محتوى تجريبي',
      live: 'مباشر',
      liveData: 'بيانات مباشرة',
      loading: 'جاري التحميل…',
      unavailable: 'غير متاح',
      fallback: 'وضع احتياطي'
    },
    home: {
      eyebrow: 'مركز معلومات فورتنايت',
      title: 'كل شيء في مكان واحد.',
      body: 'تابع المتجر، استكشف العناصر، وخلك على اطلاع من خلال تجربة فورتنايت بسيطة وواضحة.',
      shopButton: 'استكشف المتجر',
      newsButton: 'آخر التحديثات',
      briefing: 'ملخص فورتنايت',
      briefingBody: 'نظرة سريعة على أهم الأشياء اليوم.',
      shopTitle: 'معاينة المتجر',
      newsTitle: 'آخر الأخبار',
      statusTitle: 'حالة Fortnite',
      statusBody: 'جاري التحقق من حالة الخدمات الرسمية لدى Epic Games.',
      statusLoading: 'جاري التحقق من الحالة…',
      statusUnavailable: 'حالة الخدمات غير متاحة مؤقتًا',
      statusOperational: 'خدمات Fortnite تعمل بشكل طبيعي',
      statusAttention: 'يوجد تحديث يحتاج الانتباه في حالة Fortnite',
      statusLive: 'بيانات مباشرة من Epic Games Public Status',
      sourceCount: 'مصادر موثوقة مخطط لها',
      liveSources: 'فحوصات مصادر مباشرة',
      liveDesk: 'المشهد الآن',
      now: 'محدث من مصادر NEXUS',
      nexusStory: 'قصة NEXUS',
      latestUpdate: 'آخر تحديث',
      shopFreshness: 'آخر تحديث للمتجر',
      editorial: 'القصص المنشورة',
      seasonClock: 'العد التنازلي للموسم',
      seasonEnds: 'تاريخ النهاية الرسمي:',
      exactTimePending: 'Epic تعرض حاليًا تاريخ نهاية الموسم بتوقيت شرق أمريكا بدون ساعة محددة. NEXUS يحسب الوقت حتى بداية ذلك اليوم، وبيتحدث تلقائيًا إذا Epic أعلنت ساعة دقيقة.',
      endsToday: 'الموسم ينتهي اليوم بتوقيت شرق أمريكا.',
      ended: 'تاريخ نهاية هذا الموسم انتهى.',
      days: 'يوم',
      hours: 'ساعة',
      minutes: 'دقيقة',
      officialSeasonSource: 'المصدر الرسمي للـ Battle Pass'
    },
    notice: 'حالة خدمات Fortnite تجي من Epic Games Public Status. المتجر والعناصر وأخبار Battle Royale داخل اللعبة تمر من جهة الخادم عبر Fortnite-API، وأخبار Fortnite الرسمية تدخل NEXUS من خلال مراجعة تحريرية قبل النشر.',
    shop: {
      eyebrow: 'متجر العناصر',
      title: 'متجر العناصر',
      body: 'عرض مباشر للمتجر الحالي من جهة الخادم مع الأسعار وصور العناصر ووقت آخر تحديث لدى NEXUS.',
      lastSync: 'آخر جلب من NEXUS',
      live: 'تم ربط المتجر المباشر',
      error: 'مصدر المتجر المباشر غير متاح مؤقتًا',
      fallback: 'نعرض نسخة احتياطية للتصميم لأن مصدر المتجر المباشر غير متاح.'
    },
    news: {
      eyebrow: 'طبقة الأخبار',
      title: 'الأخبار',
      body: 'مقالات NEXUS الأصلية والملخصات المعتمدة من المصادر تظهر بجانب أخبار Battle Royale المباشرة داخل اللعبة.',
      feedLabel: 'أخبار Battle Royale داخل اللعبة عبر Fortnite-API',
      official: 'فتح أخبار Fortnite الرسمية',
      originals: 'قصص NEXUS',
      original: 'NEXUS ORIGINAL',
      externalBrief: 'ملخص مصدر'
    },
    cosmetics: {
      eyebrow: 'قاعدة العناصر',
      title: 'العناصر',
      body: 'ابحث في بيانات عناصر Fortnite المباشرة. إذا ما كتبت بحث، يعرض NEXUS أحدث العناصر المضافة.',
      search: 'ابحث عن عنصر',
      filter: 'الفئة',
      all: 'كل الفئات',
      noResults: 'ما لقينا نتائج مطابقة',
      results: 'نتائج',
      error: 'بيانات العناصر غير متاحة مؤقتًا'
    },
    events: {
      eyebrow: 'الفعاليات + التنافسي',
      title: 'الفعاليات',
      body: 'توقيت نهاية الموسم صار مباشر الآن. العدادات الإضافية للفعاليات والبطولات بتظهر فقط بعد التحقق من المصدر والمنطقة الزمنية.',
      pending: 'عداد الموسم شغال. عدادات التنافسي والفعاليات الخاصة بتبقى Pending لين نتأكد من المصدر وقواعد التوقيت.',
      sourcePlan: 'جدول المزامنة المخطط',
      newsSource: 'Fortnite News',
      statusSource: 'Fortnite Status',
      competitiveSource: 'Competitive'
    },
    settings: {
      eyebrow: 'خصص NEXUS',
      title: 'الإعدادات',
      body: 'نسخة حديثة من لوحة التخصيص الموجودة في موقع 2023، لكن بمزايا مفيدة بدل الإضافات العشوائية.',
      preferencesEyebrow: 'التفضيلات',
      preferences: 'تجربتك',
      language: 'اللغة',
      languageBody: 'اختر لغة الواجهة وخليها محفوظة على هذا الجهاز.',
      reduceMotion: 'تقليل الحركة',
      reduceMotionBody: 'يعطل أغلب الانتقالات والحركات في الواجهة.',
      compactMode: 'بطاقات مضغوطة',
      compactModeBody: 'يعرض عدد أكبر من بطاقات المتجر والعناصر في الشاشة.',
      reset: 'إعادة ضبط التفضيلات',
      legacyEyebrow: 'إحياء النسخة القديمة',
      legacyTitle: 'وش رجعنا',
      legacyBody: 'موقع Fortnite Status حق 2023 كان فيه أفكار حلوة لكن تنفيذها قديم. NEXUS يرجع الأشياء المفيدة منها ببيانات أفضل وتصميم أنظف.',
      legacyCountdowns: 'العد التنازلي',
      legacyFaq: 'الأسئلة الشائعة',
      legacyChangelog: 'سجل التحديثات',
      legacyPreferences: 'تفضيلات محفوظة',
      faqEyebrow: 'سؤال + جواب',
      faqTitle: 'الأسئلة الشائعة',
      changelogEyebrow: 'تاريخ الموقع',
      changelogTitle: 'وش تغير'
    },
    admin: {
      title: 'حالة النظام',
      body: 'عرض للقراءة فقط يوضح حالة المصادر، وسجل D1، وآخر عمليات المزامنة الناجحة.',
      readOnly: 'قراءة فقط',
      authNote: 'الصفحة محمية بـ Cloudflare Access. حاليًا هي للقراءة فقط، وأدوات التعديل بتظل مقفلة لين نكمل طبقة حماية الإدارة الجاية.',
      refresh: 'تحديث',
      unavailable: 'حالة النظام غير متاحة مؤقتًا',
      database: 'قاعدة D1',
      connected: 'متصلة',
      disconnected: 'غير متصلة',
      sources: 'المصادر السليمة',
      lastCheck: 'آخر فحص',
      healthy: 'سليم',
      degraded: 'متأثر',
      latency: 'الاستجابة',
      lastSuccess: 'آخر نجاح',
      failures: 'الإخفاقات',
      items: 'آخر عدد عناصر',
      itemsShort: 'عنصر',
      recentRuns: 'آخر عمليات المزامنة',
      noRuns: 'ما فيه عمليات مزامنة مسجلة للحين'
    },
    footer: 'موقع مستقل مساعد لفورتنايت وغير تابع لـ Epic Games.'
  }
} as const;
